/**
 * Interaction primitives shared by the route screens.
 *
 * These sit one level above the design-system primitives: where `primitives.tsx`
 * answers "how does a panel look", this answers "how does an operator change
 * what is shown". They live together rather than being redefined per screen so
 * that a filter bar in the audit log and a filter bar in the approval inbox are
 * the same object with the same keyboard behaviour — an operator learns the
 * console once.
 */

import {
  useCallback,
  useEffect,
  useId,
  useState,
  type ChangeEvent,
  type KeyboardEventHandler,
  type ReactNode,
} from 'react'
import clsx from 'clsx'
import { NavLink } from 'react-router-dom'
import {
  Checkbox,
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Description,
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle,
  Field,
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Radio,
  RadioGroup,
} from '@headlessui/react'
import { Check, ChevronsUpDown, Minus, Search, X } from 'lucide-react'
import { Icon } from './icon'
import { Button, EmptyState, ErrorState, Skeleton, type Tone } from './primitives'
import { ApiError } from '@/api/client'
import { useT } from '@/i18n/context'
import './controls.css'

/** Folds Turkish diacritics and case for accent-insensitive matching, so typing
 *  "istanbul" finds "İstanbul" and "cig" finds "çiğ" in a searchable select. */
function foldTr(s: string): string {
  return s
    .toLocaleLowerCase('tr')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

/* -------------------------------------------------------------- Segmented */

export interface SegmentedOption<T extends string> {
  value: T
  label: ReactNode
  /** Used for the accessible name when `label` is an icon or a glyph. */
  title?: string
}

/**
 * An exclusive choice between two to four views.
 *
 * Implemented as buttons with `aria-pressed` rather than radios: the control
 * changes what is displayed immediately, and a radio group implies a value that
 * will be submitted later.
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
}: {
  value: T
  options: ReadonlyArray<SegmentedOption<T>>
  onChange: (value: T) => void
  ariaLabel: string
  size?: 'sm' | 'md'
}) {
  return (
    <div className={clsx('k-seg', `k-seg--${size}`)} role="group" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={clsx('k-seg__btn', o.value === value && 'k-seg__btn--on')}
          aria-pressed={o.value === value}
          title={o.title}
          aria-label={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* -------------------------------------------------------------- RouteTabs */

export interface RouteTabItem {
  to: string
  label: string
  /** Rendered as a small count beside the label. */
  count?: number
  end?: boolean
}

/** Tabs backed by the router, so a tab is a location an operator can link to,
 *  bookmark and come back to — which is what a deep operational screen needs. */
export function RouteTabs({ items, ariaLabel }: { items: RouteTabItem[]; ariaLabel: string }) {
  return (
    <nav className="k-tabs" aria-label={ariaLabel}>
      <ul className="k-tabs__list">
        {items.map((it) => (
          <li key={it.to}>
            <NavLink
              to={it.to}
              end={it.end}
              className={({ isActive }) => clsx('k-tabs__tab', isActive && 'k-tabs__tab--on')}
            >
              {it.label}
              {it.count != null && <span className="k-tabs__count k-mono">{it.count}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}

/* ----------------------------------------------------------------- Fields */

interface BaseFieldProps {
  label: string
  hint?: string
  error?: string
  required?: boolean
  /** Hides the visible label but keeps it for assistive technology. */
  labelHidden?: boolean
}

export function TextField({
  label,
  hint,
  error,
  required,
  labelHidden,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  mono,
  disabled,
  autoFocus,
  name,
}: BaseFieldProps & {
  value: string
  onChange: (value: string) => void
  type?: 'text' | 'password' | 'search' | 'number' | 'email'
  placeholder?: string
  autoComplete?: string
  inputMode?: 'text' | 'numeric'
  mono?: boolean
  disabled?: boolean
  autoFocus?: boolean
  name?: string
}) {
  const id = useId()
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined
  return (
    <div className="k-field">
      <label className={clsx('k-field__label', labelHidden && 'k-visually-hidden')} htmlFor={id}>
        {label}
        {required && (
          <span className="k-field__req" aria-hidden>
            *
          </span>
        )}
      </label>
      <input
        id={id}
        name={name}
        className={clsx('k-input', mono && 'k-mono', error && 'k-input--invalid')}
        type={type}
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        disabled={disabled}
        autoFocus={autoFocus}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {error ? (
        <p className="k-field__error" id={`${id}-err`}>
          {error}
        </p>
      ) : hint ? (
        <p className="k-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export function TextAreaField({
  label,
  hint,
  error,
  required,
  labelHidden,
  value,
  onChange,
  rows = 4,
  placeholder,
  mono,
  autoFocus,
  onKeyDown,
}: BaseFieldProps & {
  value: string
  onChange: (value: string) => void
  rows?: number
  placeholder?: string
  mono?: boolean
  /** Focus on mount. Only for a field the operator asked for — a control that
   *  takes the keyboard on its own steals it from whatever they were doing. */
  autoFocus?: boolean
  /** So a caller can bind a submit key. A textarea's Enter is a newline, which
   *  is right; the modified Enter is what sends, and that has to be the
   *  caller's decision because only the caller knows what "send" means. */
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>
}) {
  const id = useId()
  const describedBy = error ? `${id}-err` : hint ? `${id}-hint` : undefined
  return (
    <div className="k-field">
      <label className={clsx('k-field__label', labelHidden && 'k-visually-hidden')} htmlFor={id}>
        {label}
        {required && (
          <span className="k-field__req" aria-hidden>
            *
          </span>
        )}
      </label>
      <textarea
        id={id}
        className={clsx('k-input', 'k-input--area', mono && 'k-mono', error && 'k-input--invalid')}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
      />
      {error ? (
        <p className="k-field__error" id={`${id}-err`}>
          {error}
        </p>
      ) : hint ? (
        <p className="k-field__hint" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export interface SelectOption {
  value: string
  label: string
}

/**
 * A dropdown that is a real listbox, not a native `<select>`.
 *
 * The prop surface is unchanged from the native version it replaced, so every
 * call site keeps working; underneath it is a Headless UI `Listbox` (keyboard,
 * type-ahead, aria and the floating panel come from the library), styled with
 * our own `k-select` classes. `searchable` swaps in a `Combobox` for the long
 * pools — accounts, models — where typing beats scanning; matching folds Turkish
 * diacritics so "istanbul" finds "İstanbul".
 */
export function SelectField({
  label,
  hint,
  labelHidden,
  value,
  onChange,
  options,
  disabled,
  searchable,
  placeholder,
}: BaseFieldProps & {
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<SelectOption>
  disabled?: boolean
  searchable?: boolean
  placeholder?: string
}) {
  const current = options.find((o) => o.value === value)
  return (
    <Field className="k-field" disabled={disabled}>
      <Label className={clsx('k-field__label', labelHidden && 'k-visually-hidden')}>{label}</Label>
      {searchable ? (
        <SearchableSelect
          value={value}
          onChange={onChange}
          options={options}
          placeholder={placeholder}
        />
      ) : (
        <Listbox value={value} onChange={onChange}>
          <ListboxButton className="k-input k-select__btn">
            <span className={clsx('k-select__value', !current && 'k-select__value--empty')}>
              {current ? current.label : (placeholder ?? '')}
            </span>
            <Icon icon={ChevronsUpDown} size={14} className="k-select__chev" />
          </ListboxButton>
          <ListboxOptions anchor="bottom start" className="k-select__panel">
            {options.map((o) => (
              <ListboxOption key={o.value} value={o.value} className="k-select__opt">
                <span className="k-select__opt-label">{o.label}</span>
                <Icon icon={Check} size={14} className="k-select__opt-check" />
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      )}
      {hint && <Description className="k-field__hint">{hint}</Description>}
    </Field>
  )
}

function SearchableSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string
  onChange: (value: string) => void
  options: ReadonlyArray<SelectOption>
  placeholder?: string
}) {
  const [query, setQuery] = useState('')
  const matches = query ? options.filter((o) => foldTr(o.label).includes(foldTr(query))) : options
  return (
    <Combobox
      value={value}
      onChange={(v: string | null) => {
        if (v != null) onChange(v)
      }}
      immediate
    >
      <div className="k-select__combo">
        <Icon icon={Search} size={14} className="k-select__combo-icon" />
        <ComboboxInput
          className="k-input k-select__input"
          placeholder={placeholder}
          displayValue={(v: string) => options.find((o) => o.value === v)?.label ?? ''}
          onChange={(e) => setQuery(e.target.value)}
        />
        <ComboboxButton className="k-select__combo-btn" aria-label={placeholder}>
          <Icon icon={ChevronsUpDown} size={14} />
        </ComboboxButton>
      </div>
      <ComboboxOptions anchor="bottom start" className="k-select__panel">
        {matches.map((o) => (
          <ComboboxOption key={o.value} value={o.value} className="k-select__opt">
            <span className="k-select__opt-label">{o.label}</span>
            <Icon icon={Check} size={14} className="k-select__opt-check" />
          </ComboboxOption>
        ))}
        {matches.length === 0 && <div className="k-select__empty k-dash">—</div>}
      </ComboboxOptions>
    </Combobox>
  )
}

export function CheckboxField({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  // A real checkbox widget, not a native input: the box fills with the accent
  // when checked and carries a drawn tick, so it reads the same on every OS
  // instead of inheriting the browser's chrome. Headless UI wires the label to
  // the box and handles Space/keyboard; the tick's visibility is CSS on the
  // box's own [data-checked] state.
  return (
    <Field className="k-check" disabled={disabled}>
      <Checkbox checked={checked} onChange={onChange} className="k-check__box">
        <Icon icon={Check} size={12} strokeWidth={3} className="k-check__tick" />
      </Checkbox>
      <div className="k-check__text">
        <Label className="k-check__label">{label}</Label>
        {hint && <Description className="k-check__hint">{hint}</Description>}
      </div>
    </Field>
  )
}

/**
 * A row selector: the checkbox that says "this one too", not a form field.
 *
 * It reuses [CheckboxField]'s box so a tick means the same thing everywhere in
 * the console, and differs from it in what it is *for*. A CheckboxField is one
 * decision inside a form, laid out with a label and a hint; this is a control in
 * a table cell whose meaning comes from the row it sits in, so it carries no
 * visible label at all — only an accessible one, because a screen reader still
 * has to be told which row is being selected.
 */
export function RowCheckbox({
  checked,
  onChange,
  label,
  indeterminate,
  disabled,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  /** Some but not all of the rows below are selected — the header's state. */
  indeterminate?: boolean
  disabled?: boolean
}) {
  return (
    <Checkbox
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="k-check__box k-rowcheck"
      data-indeterminate={indeterminate ? '' : undefined}
    >
      <Icon
        icon={indeterminate && !checked ? Minus : Check}
        size={12}
        strokeWidth={3}
        className="k-check__tick"
      />
    </Checkbox>
  )
}

/**
 * An exclusive choice between a few fixed answers, shown all at once.
 *
 * It looks like [Segmented] and means something different, which is the whole
 * reason it exists: Segmented switches what is on screen *now*, so it is built
 * from `aria-pressed` buttons, while this is a form value that is submitted
 * later — a radio group, with the arrow-key navigation and single tab stop a
 * radio group is required to have. Borrowing the pressed-button control for a
 * pending value would tell a screen-reader user the change had already taken
 * effect.
 *
 * Chosen over a dropdown wherever the options are three short words and the
 * screen carries several of these at once: six collapsed selects all reading
 * "Asks for approval" hide the one row that does not.
 */
export function ChoiceField({
  label,
  hint,
  labelHidden,
  value,
  options,
  onChange,
  disabled,
}: BaseFieldProps & {
  value: string
  options: ReadonlyArray<SelectOption & { tone?: Tone }>
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <Field className="k-field" disabled={disabled}>
      <Label className={clsx('k-field__label', labelHidden && 'k-visually-hidden')}>{label}</Label>
      <RadioGroup value={value} onChange={onChange} className="k-choice">
        {options.map((o) => (
          <Radio
            key={o.value}
            value={o.value}
            className={clsx('k-choice__opt', o.tone && `k-choice__opt--${o.tone}`)}
          >
            {o.label}
          </Radio>
        ))}
      </RadioGroup>
      {hint && <Description className="k-field__hint">{hint}</Description>}
    </Field>
  )
}

/* ---------------------------------------------------------------- Toolbar */

/** The filter row above a list. Wraps rather than scrolls, because a filter an
 *  operator cannot see is a filter they will forget is applied. */
export function Toolbar({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  const t = useT()
  return (
    <div className="k-toolbar" role="search" aria-label={t('common.filters')}>
      <div className="k-toolbar__filters">{children}</div>
      {actions && <div className="k-toolbar__actions">{actions}</div>}
    </div>
  )
}

/* ------------------------------------------------------------- QueryState */

/**
 * The loading / error / empty gate every data screen goes through.
 *
 * Centralised so that the three states are never skipped: a screen that renders
 * `data?.items.map(...)` shows a blank panel on failure, and a blank panel is
 * indistinguishable from "there is nothing here" — which is the single most
 * expensive ambiguity in an operations console.
 */
export function QueryState({
  pending,
  error,
  onRetry,
  empty,
  emptyTitle,
  emptyHint,
  emptyAction,
  skeleton,
  children,
}: {
  pending: boolean
  error: unknown
  onRetry?: () => void
  empty?: boolean
  emptyTitle?: string
  emptyHint?: string
  emptyAction?: ReactNode
  skeleton?: ReactNode
  children: ReactNode
}) {
  const t = useT()
  const describe = useApiErrorMessage()

  if (pending) return <>{skeleton ?? <DefaultSkeleton />}</>

  if (error) {
    const { title, hint } = describe(error)
    return <ErrorState title={title} hint={hint} onRetry={onRetry} retryLabel={t('action.retry')} />
  }

  if (empty) {
    return (
      <EmptyState title={emptyTitle ?? t('state.empty')} hint={emptyHint} action={emptyAction} />
    )
  }

  return <>{children}</>
}

function DefaultSkeleton() {
  return (
    <div className="k-skelstack" aria-hidden>
      <Skeleton height={18} />
      <Skeleton height={18} width="82%" />
      <Skeleton height={18} width="64%" />
    </div>
  )
}

/**
 * Turns an unknown thrown value into a title and a remedy.
 *
 * Every branch names an action. "Something went wrong" tells an operator
 * nothing they did not already know from the blank screen.
 */
export function useApiErrorMessage() {
  const t = useT()
  return useCallback(
    (err: unknown): { title: string; hint: string } => {
      if (err instanceof ApiError) {
        if (err.status === 0) return { title: t('error.network'), hint: t('error.loadFailedHint') }
        // A 403 that carries its own message says something more useful than
        // "you do not have permission" — a rejected setup token, a disabled
        // account, a failed CSRF check. Titling all three identically and
        // demoting the real reason to a hint is how a missing form field came
        // to read as an authorisation fault.
        if (err.status === 403) {
          return err.message
            ? { title: err.message, hint: t('error.forbiddenHint') }
            : { title: t('error.forbidden'), hint: t('error.forbiddenHint') }
        }
        if (err.status === 404) return { title: t('error.notFound'), hint: err.message }
        return {
          title: err.message || t('error.unexpected'),
          hint: err.correlationId
            ? `${t('audit.correlation')}: ${err.correlationId}`
            : t('error.loadFailedHint'),
        }
      }
      return { title: t('error.loadFailed'), hint: t('error.loadFailedHint') }
    },
    [t],
  )
}

/* ----------------------------------------------------------------- Notice */

/** An inline result banner for an action the operator just took. Inline rather
 *  than a floating toast: a toast that disappears takes the outcome with it. */
export function Notice({ tone = 'info', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <p className={clsx('k-notice', `k-notice--${tone}`)} role="status">
      {children}
    </p>
  )
}

/* ------------------------------------------------------------- CopyButton */

/** Copies a machine value. Ids and hashes exist to be pasted somewhere else,
 *  and asking an operator to select 64 hex characters by hand is hostile. */
export function CopyButton({ value, ariaLabel }: { value: string; ariaLabel: string }) {
  const t = useT()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1600)
    return () => clearTimeout(timer)
  }, [copied])

  return (
    <button
      type="button"
      className="k-copy"
      aria-label={ariaLabel}
      title={copied ? t('common.copied') : t('common.copy')}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(
          () => setCopied(true),
          // A clipboard denied by permissions is not worth an error banner; the
          // value is still on screen and selectable.
          () => undefined,
        )
      }}
    >
      {copied ? '✓' : '⧉'}
    </button>
  )
}

/* ----------------------------------------------------------------- Dialog */

/**
 * A modal for the few flows that genuinely interrupt: adding a host, creating a
 * mission. Everything else in Kimene is a page, because a modal cannot be
 * linked to and an operations console is full of things worth linking to.
 */
/**
 * The one modal in the console.
 *
 * The focus-trap, the Escape handler, the return-focus-on-close and the portal
 * are Headless UI's — hand-rolling them (as this once did) is exactly the kind
 * of accessibility detail that rots. The prop surface is unchanged, so every
 * dialog built on it inherits the corner-bracket frame and the display-face
 * title without touching its own code.
 */
export function Dialog({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  /** Room for content the default column cannot hold — a terminal, whose lines
   *  are as long as the provider's authorisation URL and must not wrap. */
  wide?: boolean
}) {
  const t = useT()
  return (
    <HeadlessDialog open onClose={onClose} className="k-dialog__root">
      <div className="k-dialog__scrim" aria-hidden="true" />
      <div className="k-dialog__wrap">
        <DialogPanel className={`k-dialog k-frame${wide ? ' k-dialog--wide' : ''}`}>
          <header className="k-dialog__head">
            <DialogTitle className="k-dialog__title">{title}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('action.close')}>
              <Icon icon={X} size={16} />
            </Button>
          </header>
          <div className="k-dialog__body">{children}</div>
          {footer && <footer className="k-dialog__foot">{footer}</footer>}
        </DialogPanel>
      </div>
    </HeadlessDialog>
  )
}

/* -------------------------------------------------------------- Diff lines */

